import { useMemo } from "react";
import { TrendingUp, Calendar } from "lucide-react";
import clsx from "clsx";

import { StatCard } from "./StatCard";
import { SlotMachineCurrency } from "./SlotMachineCurrency";
import { NarrativeCallout } from "./NarrativeCallout";
import { formatCurrency, getCompletedMonthsForComparison } from "./utils";
import { useMoneySaved } from "./MoneySavedContext";
import { useScrollReveal } from "./useScrollReveal";
import { formatMonthLong } from "./api";

export const MoneyStatCardGrid = ({
  showNarrative = false,
}: {
  showNarrative?: boolean;
}) => {
  const { months } = useMoneySaved();
  const { ref, isVisible } = useScrollReveal(0.2);

  const { current: compareMonth, previous: baseMonth } =
    getCompletedMonthsForComparison(months);

  const latestMonth = months.length > 0 ? months[months.length - 1] : null;

  const currentMonthLabel = latestMonth
    ? formatMonthLong(latestMonth.month)
    : "";

  const compareMonthShort = compareMonth
    ? new Date(compareMonth.month).toLocaleDateString("en-US", {
        month: "long",
        timeZone: "UTC",
      })
    : "";

  const avgMonthlySavings = useMemo(() => {
    if (months.length === 0) return 0;
    const total = months.reduce((sum, m) => sum + m.savings, 0);
    return Math.round(total / months.length);
  }, [months]);

  const savingsChange = useMemo(() => {
    if (!compareMonth || !baseMonth) return null;
    if (baseMonth.savings === 0) return null;
    return Math.round(
      ((compareMonth.savings - baseMonth.savings) / baseMonth.savings) * 100,
    );
  }, [compareMonth, baseMonth]);

  const latestMonthShort = latestMonth
    ? new Date(latestMonth.month).toLocaleDateString("en-US", {
        month: "long",
        timeZone: "UTC",
      })
    : "";

  const narrativeHeadline = useMemo(() => {
    if (!latestMonth) return null;
    if (savingsChange !== null && savingsChange > 0 && compareMonthShort) {
      return (
        <>
          {compareMonthShort} was your{" "}
          <span className="text-blue-600">best month yet.</span>
        </>
      );
    }
    return (
      <>
        Here's how <span className="text-blue-600">{latestMonthShort}</span>{" "}
        breaks down.
      </>
    );
  }, [savingsChange, compareMonthShort, latestMonthShort, latestMonth]);

  const narrativeSubtext = useMemo(() => {
    if (!latestMonth) return null;
    const savings = latestMonth.savings;
    const extraConversions = latestMonth.extra_conversions;
    if (savingsChange !== null && savingsChange > 0 && compareMonth) {
      const cSavings = compareMonth.savings;
      const cExtra = compareMonth.extra_conversions;
      if (cExtra > 0) {
        return `You saved ${formatCurrency(cSavings)} in ${compareMonthShort} — with ${Math.round(cExtra)} extra conversions from CAPI efficiency.`;
      }
      return `You saved ${formatCurrency(cSavings)} in ${compareMonthShort} — ${savingsChange}% more than the month before.`;
    }
    if (extraConversions > 0) {
      return `You saved ${formatCurrency(savings)} this month. CAPI efficiency drove ${Math.round(extraConversions)} extra conversions.`;
    }
    return `You saved ${formatCurrency(savings)} this month.`;
  }, [savingsChange, latestMonth, compareMonth, compareMonthShort]);

  return (
    <div ref={ref} className="flex flex-col gap-8">
      {showNarrative && narrativeHeadline && narrativeSubtext && (
        <NarrativeCallout
          headline={narrativeHeadline}
          subtext={narrativeSubtext}
        />
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div
          className={clsx(
            "transition-all duration-700",
            isVisible ? "animate-slide-in-left" : "opacity-0",
          )}
          style={{ animationDelay: showNarrative ? "400ms" : "0ms" }}
        >
          <StatCard
            icon={Calendar}
            label="This Month"
            value={
              <SlotMachineCurrency
                value={latestMonth?.savings ?? 0}
                trigger={isVisible}
                formatFn={formatCurrency}
              />
            }
            subtext={currentMonthLabel}
            color="blue"
            tooltip="This month's savings = (Baseline CPA − Current CPA) × low-funnel conversions."
          />
        </div>
        <div
          className={clsx(
            "transition-all duration-700",
            isVisible ? "animate-slide-in-right" : "opacity-0",
          )}
          style={{ animationDelay: showNarrative ? "600ms" : "0ms" }}
        >
          <StatCard
            icon={TrendingUp}
            label="Avg Monthly Savings"
            value={
              <SlotMachineCurrency
                value={avgMonthlySavings}
                trigger={isVisible}
                formatFn={formatCurrency}
              />
            }
            subtext={`Last ${months.length} months`}
            color="orange"
            tooltip="Average of all monthly savings since tracking began."
          />
        </div>
      </div>
    </div>
  );
};
